/* Shared mount helper for the v5 pages (home, artist pages, artist interview).
   The approved prototypes are plain HTML + CSS + script; each page mounts that markup into its own
   scoped root (.ti-home / .ti-artist / .ti-iv) and runs the original script, then cleans up on unmount. */

const FONT_ID = 'ti-v5-fonts';
const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Big+Shoulders+Display:wght@600;700;800;900&family=Playfair+Display:ital,wght@1,500;1,700;1,800&family=Manrope:wght@400;500;600;700;800&display=swap';

type ShellOptions = {
  title: string;
  description?: string;
  scrollPadding?: string;
  rootFontSize?: string;
  background?: string;
};

export function ensureFonts() {
  if (document.getElementById(FONT_ID)) return;
  const link = document.createElement('link');
  link.id = FONT_ID;
  link.rel = 'stylesheet';
  link.href = FONT_HREF;
  document.head.appendChild(link);
}

export function mountShell(root: HTMLElement, navigate: (to: string) => void, opts: ShellOptions) {
  ensureFonts();
  const html = document.documentElement;
  const prev = {
    title: document.title,
    scrollBehavior: html.style.scrollBehavior,
    scrollPaddingTop: html.style.scrollPaddingTop,
    overflowX: html.style.overflowX,
    fontSize: html.style.fontSize,
    background: html.style.background,
  };
  document.title = opts.title;
  if (opts.description) {
    let meta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.content = opts.description;
  }
  html.style.scrollBehavior = 'smooth';
  html.style.scrollPaddingTop = opts.scrollPadding || '0px';
  html.style.overflowX = 'hidden';
  if (opts.rootFontSize) html.style.fontSize = opts.rootFontSize;
  if (opts.background) html.style.background = opts.background;

  // Same-site links ("/artist/casso", "/book-now", "/#crew") navigate inside the app instead of reloading.
  const onClick = (e: MouseEvent) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = (e.target as Element | null)?.closest?.('a');
    if (!a) return;
    const href = a.getAttribute('href') || '';
    if (!href.startsWith('/') || href.startsWith('//') || (a.getAttribute('target') || '') === '_blank') return;
    e.preventDefault();
    const url = new URL(href, window.location.origin);
    if (url.pathname === window.location.pathname && url.hash) {
      const el = document.getElementById(url.hash.slice(1));
      if (el) el.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    navigate(url.pathname + url.search + url.hash);
  };
  root.addEventListener('click', onClick);

  // Arriving with a hash (e.g. /#crew from an artist page): jump to it after the page is built.
  let hashTimer = 0;
  if (window.location.hash.length > 1) {
    const id = decodeURIComponent(window.location.hash.slice(1));
    hashTimer = window.setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView();
    }, 120);
  }

  return () => {
    window.clearTimeout(hashTimer);
    root.removeEventListener('click', onClick);
    document.title = prev.title;
    html.style.scrollBehavior = prev.scrollBehavior;
    html.style.scrollPaddingTop = prev.scrollPaddingTop;
    html.style.overflowX = prev.overflowX;
    html.style.fontSize = prev.fontSize;
    html.style.background = prev.background;
  };
}
